import React, { createContext, useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import { UserContext } from "./UserContext";
import { initEcho } from "../echo";

export const NotificationContext = createContext();

const NotificationProvider = ({ children }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { user } = useContext(UserContext);
  const [unreadCount, setUnreadCount] = useState(0);
  const [liveNotification, setLiveNotification] = useState(null);
  const fetchRequestIdRef = useRef(0);

  const fetchUnreadCount = useCallback(async () => {
    const reqId = ++fetchRequestIdRef.current;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setUnreadCount(0);
        return;
      }
      const res = await fetch(`${apiUrl}/user/notification/get?is_read=0`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (!res.ok) {
        setUnreadCount(0);
        return;
      }
      const data = await res.json();
      if (reqId !== fetchRequestIdRef.current) return;
      if (data && data.status) {
        setUnreadCount(data.meta?.total ?? (Array.isArray(data.data) ? data.data.length : 0));
      } else {
        setUnreadCount(0);
      }
    } catch (e) {
      console.debug("[NotificationContext] fetchUnreadCount error:", e);
    }
  }, [apiUrl]);

  // Fetch on login / user change
  useEffect(() => {
    if (user) fetchUnreadCount();
    else setUnreadCount(0);
  }, [user, fetchUnreadCount]);

  // Direct Pusher subscription
  useEffect(() => {
    if (!user?.id) return;

    // Initialize Echo/Pusher only when we have a logged-in user
    let cancelled = false;
    let channelRef = null;
    let handlerRef = null;

    initEcho().then(() => {
      if (cancelled || !window.Echo) return;

      channelRef = window.Echo.private(`user.${user.id}`);

      handlerRef = (e) => {
        console.log("[NotificationContext] Pusher notification received:", e.title);
        setUnreadCount((prev) => prev + 1);
        setLiveNotification(e);
        window.dispatchEvent(new CustomEvent("app:live-notification", { detail: e }));
      };

      channelRef.listen(".PrivateNotification", handlerRef);
    });

    return () => {
      cancelled = true;
      if (channelRef && handlerRef) {
        channelRef.stopListening(".PrivateNotification", handlerRef);
      }
    };
  }, [user]);

  // Polling fallback: check every 15 seconds (only when page is visible)
  useEffect(() => {
    if (!user) return;

    // Fetch immediately when page becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Poll every 15 seconds, but only when page is visible
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    }, 15000);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, fetchUnreadCount]);

  // Cross-tab sync via storage events
  useEffect(() => {
    const onStorage = (ev) => {
      if (ev.key === "lastLiveNotification" && ev.newValue) {
        try {
          const parsed = JSON.parse(ev.newValue);
          if (parsed && parsed.data) {
            setUnreadCount((prev) => prev + 1);
            setLiveNotification(parsed.data);
          }
        } catch (err) {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const resetUnreadCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const contextValue = useMemo(() => ({
    unreadCount,
    setUnreadCount,
    liveNotification,
    setLiveNotification,
    fetchUnreadCount,
    resetUnreadCount,
  }), [unreadCount, liveNotification, fetchUnreadCount, resetUnreadCount]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
